import { useState, useEffect } from 'react';
import { C, S, BASE } from '../constants';
import { fmtUAH } from '../utils';
import Img from '../components/Img';
import ProdLink from '../components/ProdLink';

function ReconcileSection({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    fetch(`${BASE}/api/balance/reconcile?${params}`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("crm_token") }
    }).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  };

  if (!open) {
    return (
      <div style={{ ...S.card, marginTop: 16, cursor: "pointer", opacity: 0.7 }} onClick={() => { setOpen(true); load(); }}>
        <div style={{ fontSize: 13, color: C.text3 }}>
          Показати звірку витрат (оцінка vs факт Rozetka)
        </div>
      </div>
    );
  }

  const est = data?.our_estimates;
  const aggs = data?.aggregates || [];

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Звірка витрат: оцінка vs Rozetka</div>
        <span style={{ fontSize: 11, color: C.text3 }}>
          {data?.period?.from} — {data?.period?.to}
          {data?.entries_fetched > 0 && <> · {data.entries_fetched} транзакцій</>}
        </span>
      </div>
      {loading && <div style={{ color: C.text3, fontSize: 13 }}>Завантаження...</div>}
      {data && !loading && (
        <>
          {/* Our estimates */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: C.text3, marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Наша оцінка (з замовлень CRM)</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
              {[
                { label: "Замовлень", value: est?.order_count, color: C.text1, noUAH: true },
                { label: "Виручка", value: est?.revenue, color: C.green },
                { label: "Комісія Rozetka", value: -est?.commission_est, color: C.yellow },
                { label: "Доставка (FF+FBS)", value: -est?.delivery_est, color: C.purple },
                { label: "3% платіжки", value: -est?.payment_fee_est, color: C.blue },
              ].map(({ label, value, color, noUAH }) => (
                <div key={label} style={{ background: C.bg1, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: C.text4, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color }}>
                    {noUAH ? value : (value != null ? (value >= 0 ? "+" : "") + fmtUAH(Math.abs(value)) : "—")}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rozetka ledger aggregates */}
          {aggs.length > 0 ? (
            <>
              <div style={{ fontSize: 12, color: C.text3, marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Дані Rozetka (реальний леджер)
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ ...S.th(), textAlign: "left" }}>Тип операції</th>
                    <th style={{ ...S.th(), textAlign: "right" }}>Кількість</th>
                    <th style={{ ...S.th(), textAlign: "right" }}>Сума, ₴</th>
                  </tr>
                </thead>
                <tbody>
                  {aggs.map((a, i) => (
                    <tr key={i}>
                      <td style={{ ...S.td }}>
                        <span>{a.type}</span>
                        <span style={{ fontSize: 10, color: C.text4, marginLeft: 8 }}>
                          {a.sample_keys.slice(0, 5).join(", ")}
                        </span>
                      </td>
                      <td style={{ ...S.td, textAlign: "right", color: C.text3 }}>{a.count}</td>
                      <td style={{ ...S.td, textAlign: "right", color: a.total >= 0 ? C.green : C.pink, fontWeight: 600 }}>
                        {a.total >= 0 ? "+" : ""}{fmtUAH(Math.abs(a.total))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div style={{ fontSize: 13, color: C.text3, padding: "12px 0" }}>
              {data.entries_fetched === 0
                ? "Rozetka /balances/search не повернула транзакцій для цього періоду"
                : "Не вдалось отримати дані леджера"}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ProfitPage({ abc, loss, dateFrom, dateTo }) {
  const [promoLift, setPromoLift] = useState(null);
  useEffect(() => {
    fetch(BASE + "/api/analytics/promo-lift", {
      headers: { Authorization: "Bearer " + localStorage.getItem("crm_token") }
    }).then(r => r.json()).then(d => setPromoLift(d.items || [])).catch(() => {});
  }, []);

  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 16 }}>Прибутковість</div>
      <div style={{ ...S.card, marginBottom: 16, background: "rgba(96,165,250,.06)", border: "1px solid rgba(96,165,250,.2)" }}>
        <div style={{ fontSize: 13, color: C.blue, fontWeight: 600, marginBottom: 10 }}>ℹ Формула розрахунку прибутку</div>
        <div style={{ fontSize: 13, color: C.text2, marginBottom: 8, fontFamily: "monospace",
          background: C.bg1, padding: "8px 12px", borderRadius: 8 }}>
          Прибуток = Ціна - (Комісія% + 3% платіжки) - Доставка(FF=42 / FBS=49 грн) - Собівартість - Доп. витрати
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13, color: C.text2, lineHeight: 1.8 }}>
          <div>• <b style={{ color: C.yellow }}>Комісія Rozetka</b> — % від ціни (12-25%)</div>
          <div>• <b style={{ color: C.yellow }}>3% платіжки</b> — завжди, на всі замовлення</div>
          <div>• <b style={{ color: C.purple }}>Доставка FBS = 49 грн</b> — партнерська доставка</div>
          <div>• <b style={{ color: C.purple }}>Доставка FF = 42 грн</b> — пакування 15 + видача 27.48</div>
          <div>• <b style={{ color: C.pink }}>Промо ціна</b> — рахується по фактичній ціні продажу</div>
          <div>• <b style={{ color: C.pink }}>Промо в мінус</b> — якщо промо нижче собів+комісії = збиток</div>
        </div>
      </div>
      {(() => {
        const allProducts = [...(abc || []), ...(loss || [])];
        const totalAdSpend = allProducts.reduce((s, p) => s + (p.ad_spend || 0), 0);
        const totalProfit = allProducts.reduce((s, p) => s + (p.profit_total || 0), 0);
        if (totalAdSpend <= 0) return null;
        const profitAfterAds = totalProfit - totalAdSpend;
        return (
          <div style={{ ...S.card, marginBottom: 16, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
            background: "rgba(251,191,36,.05)", border: "1px solid rgba(251,191,36,.2)" }}>
            <span style={{ fontSize: 13, color: C.text2 }}>
              📢 Витрати на рекламу:
            </span>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.yellow }}>
              -{fmtUAH(totalAdSpend)}
            </span>
            <span style={{ fontSize: 13, color: C.text3 }}>→</span>
            <span style={{ fontSize: 13, color: C.text2 }}>Прибуток після реклами:</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: profitAfterAds >= 0 ? C.green : C.pink }}>
              {profitAfterAds >= 0 ? "+" : ""}{fmtUAH(profitAfterAds)}
            </span>
            <span style={{ fontSize: 11, color: C.text4 }}>(від рекл. товарів)</span>
          </div>
        );
      })()}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 16 }}>
        <div style={S.card}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.green, marginBottom: 12 }}>✅ Найприбутковіші</div>
          {abc.filter(p => p.cost_price > 0 && p.profit_per_unit > 0)
            .sort((a,b) => b.profit_total - a.profit_total)
            .slice(0, 8)
            .map((p, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center",
                padding: "7px 0", borderBottom: `1px solid rgba(26,39,68,.4)` }}>
                <Img src={p.image_url} size={28} />
                <ProdLink name={p.name} article={p.article} productId={p.product_id} style={{ fontSize: 12, color: C.text2, flex: 1 }} />
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>+{fmtUAH(p.profit_total)}</div>
                  <div style={{ fontSize: 11, color: C.text3 }}>{p.margin_pct.toFixed(1)}% маржа</div>
                </div>
              </div>
            ))
          }
        </div>
        <div style={S.card}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.pink, marginBottom: 12 }}>❌ Збиткові товари</div>
          {loss.length === 0
            ? <div style={{ color: C.text3 }}>Збиткових немає (або не введена собівартість)</div>
            : loss.map((p, i) => (
              <div key={i} style={{ padding: "8px 0", borderBottom: `1px solid rgba(26,39,68,.4)` }}>
                <ProdLink name={p.name} article={p.article} productId={p.product_id} style={{ fontSize: 12, marginBottom: 4 }} />
                <div style={{ display: "flex", gap: 10, fontSize: 11, flexWrap: "wrap" }}>
                  <span style={{ color: C.text3 }}>Ціна: {fmtUAH(p.avg_price)}</span>
                  <span style={{ color: C.yellow }}>Ком.: {fmtUAH(p.commission_amt)}</span>
                  <span style={{ color: C.yellow }}>Пл.: {fmtUAH(p.payment_fee)}</span>
                  <span style={{ color: C.purple }}>Дост.: {fmtUAH(p.delivery_cost_used)}</span>
                  <span style={{ color: C.text3 }}>Собів.: {fmtUAH(p.cost_price)}</span>
                  {p.extra_cost > 0 && <span style={{ color: C.text3 }}>Доп.: {fmtUAH(p.extra_cost)}</span>}
                  <span style={{ color: C.pink, fontWeight: 700 }}>{fmtUAH(p.profit_per_unit)}/шт</span>
                </div>
                {p.promo_count > 0 && p.avg_regular_price > p.avg_price + 1 && (
                  <div style={{ fontSize: 11, color: C.pink, marginTop: 3 }}>
                    ⚠ Промо ({p.promo_count} шт.): {fmtUAH(p.avg_price)} (зв. {fmtUAH(p.avg_regular_price)})
                  </div>
                )}
              </div>
            ))
          }
        </div>
      </div>

      {/* Ефект промо */}
      {promoLift !== null && promoLift.length > 0 && (
        <div style={{ ...S.card, marginTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>📊 Ефект промо-ціни</div>
          <div style={{ fontSize: 12, color: C.text3, marginBottom: 14 }}>
            Порівняння темпу продажів на промо vs без. Мінімум 3 дні кожного режиму за останні 90 днів.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {promoLift.map(p => {
              const isPos = p.lift_pct >= 0;
              return (
                <div key={p.rozetka_id} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "6px 10px", borderRadius: 8,
                  background: isPos ? `${C.green}0d` : `${C.pink}0d`,
                  border: `1px solid ${isPos ? C.green : C.pink}30`,
                }}>
                  <Img src={p.image_url} size={28} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <ProdLink name={p.name} article={p.article} productId={p.rozetka_id}
                      style={{ fontSize: 12, color: C.text1, fontWeight: 500, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} />
                    <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>
                      Промо: {p.promo_velocity}/дн ({p.promo_days} дн) · Звичайна: {p.normal_velocity}/дн ({p.normal_days} дн)
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: isPos ? C.green : C.pink }}>
                      {isPos ? "+" : ""}{p.lift_pct}%
                    </div>
                    <div style={{ fontSize: 11, color: C.text3 }}>
                      {p.catalog_promo_price ? fmtUAH(p.catalog_promo_price) : "—"} / {fmtUAH(p.catalog_price)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Звірка витрат з леджером Rozetka */}
      <ReconcileSection dateFrom={dateFrom} dateTo={dateTo} />
    </div>
  );
}
